import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import { Lock, Eye, EyeOff, Loader2, CheckCircle2, AlertCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const REQS = [
  { id: "len", label: "Al menos 8 caracteres", test: (p) => p.length >= 8 },
  { id: "upper", label: "Una mayúscula", test: (p) => /[A-Z]/.test(p) },
  { id: "lower", label: "Una minúscula", test: (p) => /[a-z]/.test(p) },
  { id: "digit", label: "Un número", test: (p) => /[0-9]/.test(p) },
];

export default function ChangePassword() {
  const { refreshUser, user } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState({ a: false, b: false, c: false });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const allOk = REQS.every((r) => r.test(next)) && next === confirm && next !== current;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    if (next !== confirm) {
      setErrors({ confirm: "Las contraseñas no coinciden." });
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Contraseña actualizada correctamente");
      await refreshUser();
      // Redirige al home de su rol
      const home = {
        admin: "/admin/franquicias",
        franquiciado: "/franquiciado/franquicia",
        docente: "/docente/mis-estudiantes",
        estudiante: "/estudiante/perfil",
      }[data?.role || user?.role] || "/";
      navigate(home, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.field) {
        setErrors({ [detail.field === "current_password" ? "current" : "next"]: detail.message });
      } else {
        toast.error(formatApiErrorDetail(detail) || err.message);
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F2F4FA] via-white to-[#FFF7DA] flex items-center justify-center p-6" data-testid="change-password-page">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-[#E1E5F0] overflow-hidden">
        <div className="px-8 py-6 border-b border-[#E1E5F0] flex items-center gap-3">
          <Logo size={28} />
          <div className="ml-auto">
            <span className="jasq-pill bg-[#FFE9F0] text-[#A52258] text-[10px]">
              <ShieldCheck size={11} /> Cambio obligatorio
            </span>
          </div>
        </div>
        <div className="px-8 pt-6 pb-2">
          <p className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-[#A43790]">Primer ingreso</p>
          <h1 className="font-display text-2xl font-extrabold text-[#1F2547] mt-1">Crea tu nueva contraseña</h1>
          <p className="text-xs text-[#6E7591] mt-1 leading-relaxed">
            Por seguridad debes cambiar la clave provisional que recibiste en tu correo. No podrás reutilizar tus 3 últimas contraseñas.
          </p>
        </div>
        <form onSubmit={onSubmit} className="px-8 pb-8 pt-4 space-y-4">
          <Field
            label="Contraseña actual (la del correo)"
            value={current}
            setValue={setCurrent}
            show={show.a}
            toggle={() => setShow((s) => ({ ...s, a: !s.a }))}
            error={errors.current}
            testid="cp-current"
          />
          <Field
            label="Nueva contraseña"
            value={next}
            setValue={setNext}
            show={show.b}
            toggle={() => setShow((s) => ({ ...s, b: !s.b }))}
            error={errors.next}
            testid="cp-next"
          />
          <div className="grid grid-cols-2 gap-1.5" data-testid="cp-reqs">
            {REQS.map((r) => {
              const ok = r.test(next);
              return (
                <span key={r.id} className={`text-[11px] font-bold inline-flex items-center gap-1 ${ok ? "text-[#3FA84D]" : "text-[#6E7591]"}`}>
                  {ok ? <CheckCircle2 size={11} /> : <AlertCircle size={11} />} {r.label}
                </span>
              );
            })}
          </div>
          <Field
            label="Confirma la nueva contraseña"
            value={confirm}
            setValue={setConfirm}
            show={show.c}
            toggle={() => setShow((s) => ({ ...s, c: !s.c }))}
            error={errors.confirm}
            testid="cp-confirm"
          />
          <button
            type="submit"
            disabled={loading || !allOk}
            data-testid="cp-submit"
            className="jasq-btn-primary w-full justify-center inline-flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={14} />}
            <span>{loading ? "Guardando…" : "Cambiar contraseña"}</span>
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, setValue, show, toggle, error, testid }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] uppercase tracking-wider font-extrabold text-[#6E7591]">{label}</label>
      <div className="relative">
        <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7591]" />
        <input
          data-testid={testid}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className={`w-full pl-9 pr-10 py-3 rounded-xl border-2 ${error ? "border-[#EF4444]" : "border-[#E1E5F0]"} outline-none focus:border-[#A43790] font-medium text-sm`}
          autoComplete="new-password"
        />
        <button type="button" onClick={toggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6E7591]" tabIndex={-1}>
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
      {error && (
        <p className="text-[11px] text-[#EF4444] font-bold inline-flex items-center gap-1" data-testid={`${testid}-error`}>
          <AlertCircle size={11} /> {error}
        </p>
      )}
    </div>
  );
}
